/**
 * ADR-0154 §6.3 C5 — the semantic-model vocabularies, in ONE console home,
 * held against the hub by a parity gate.
 *
 * There were three copies. Two were labelled "Aligned with hub …", which is a
 * comment, not a mechanism — and the third had already fallen out of
 * alignment: `SemanticModelCanvas.tsx` knew three display modes where the hub
 * validates four, so a model saved with `display_mode: "profile"` — a mode the
 * hub accepts and the designer offers — silently rendered as Columns. The
 * `normalizeDisplayMode` safe-default made the drop invisible.
 *
 * Every set here is asserted equal to the hub's own in `semanticModelVocab.test.ts`,
 * by reading the hub source. The label maps stay console-owned and are typed
 * `Record<Union, …>` so a new hub value fails the console typecheck once the
 * union is updated — mechanism 3 of the §6.3.1 doctrine.
 */

export const SEMANTIC_DISPLAY_MODES = ["name", "keys", "columns", "profile"] as const;
export type SemanticDisplayMode = (typeof SEMANTIC_DISPLAY_MODES)[number];

export const SEMANTIC_FIELD_DATA_TYPES = [
  "varchar",
  "int",
  "decimal",
  "boolean",
  "date",
  "datetime",
  "text",
  "blob",
] as const;
export type SemanticFieldDataType = (typeof SEMANTIC_FIELD_DATA_TYPES)[number];

/**
 * ADR-0212 (ruling 19): a field's own access — the floor — is allow or
 * restrict. The four old classes were mapped by hub migration 282; this list
 * and the hub validator's are held equal by the parity gate.
 */
export const SEMANTIC_SECURITY_CLASSES = ["allow", "restrict"] as const;
export type SemanticSecurityClass = (typeof SEMANTIC_SECURITY_CLASSES)[number];

export const SEMANTIC_EXPOSURE_MODES = ["full", "masked", "aggregate_only", "restricted"] as const;
export type SemanticExposureMode = (typeof SEMANTIC_EXPOSURE_MODES)[number];

const DISPLAY_MODE_SET = new Set<string>(SEMANTIC_DISPLAY_MODES);

/**
 * Coerce a stored display mode to a known one.
 *
 * The safe default stays — an unreadable value must not break the canvas — but
 * it can no longer swallow a VALID mode, because the set it checks against is
 * now the same one the hub validates and the designer offers.
 */
export function normalizeSemanticDisplayMode(raw: string | undefined | null): SemanticDisplayMode {
  return raw && DISPLAY_MODE_SET.has(raw) ? (raw as SemanticDisplayMode) : "columns";
}

export function isSemanticDisplayMode(raw: unknown): raw is SemanticDisplayMode {
  return typeof raw === "string" && DISPLAY_MODE_SET.has(raw);
}

/** Console-owned presentation. Exhaustive over the union by construction. */
export const SEMANTIC_DISPLAY_MODE_LABEL: Record<SemanticDisplayMode, string> = {
  name: "Name only",
  keys: "Keys",
  columns: "Columns",
  profile: "Profile",
};

/** Console-owned presentation of a field's access. Exhaustive over the union by construction. */
export const SEMANTIC_SECURITY_CLASS_LABEL: Record<SemanticSecurityClass, string> = {
  allow: "Allow",
  restrict: "Restrict",
};

/** The four classes allow / restrict replaced (ADR-0212 §6.1) — still read from old workbooks and templates. */
export const LEGACY_SECURITY_CLASSES = ["low", "confidential", "restricted", "prohibited"] as const;

/**
 * Map any stored or imported access value to allow / restrict by the table
 * hub migration 282 applied (ADR-0212 §6.1): low and confidential → allow,
 * restricted and prohibited → restrict, allow and restrict kept, anything
 * else or nothing → allow. Case and surrounding space are ignored, as in the
 * migration.
 */
export function legacyAccessValue(raw: unknown): SemanticSecurityClass {
  const v = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return v === "restrict" || v === "restricted" || v === "prohibited" ? "restrict" : "allow";
}
